/* eslint-disable prefer-const */
import { Injectable } from "@angular/core";
import { CalculatingScenario } from "../../enum/calculating-scenario/calculation-scenario.enum";
import { MarketScenario } from "../../enum/market-scenario/market-scenario.enum";
import { ComputingParameter } from "../../models/computing-parameter/computing-parameter.model";
import { OrderMarketTypeEnum } from "../../enum/orders/market-type.enum";

/**
 * Serviço para encapsular todos os cálculos da área de bolsa da plataforma
 */
@Injectable()
export class ComputerService{

    /**
     * A partir de um cenário de cálculo efetua os cáculos usando os parâmetros recebidos
     * @param marketScenario cenário de tipo de ativo (Ação/Opção ou Futuro PTAX/Não PTAX)
     * @param computingScenario cenário de cálculo
     * @param args argumentos para o cálculo
     */
    calculate(marketScenario: MarketScenario, calculatingScenario: CalculatingScenario, parameter: ComputingParameter){

        if(marketScenario == MarketScenario.StockAndOptions){
            switch(calculatingScenario){  

                case CalculatingScenario.Buy:
                case CalculatingScenario.BuyStrategy:
                    return this.ComputeModelOne(parameter.amount, parameter.unitPrice, parameter.quoteFactor);

                case CalculatingScenario.BuyDayTrade:
                case CalculatingScenario.Sell:
                case CalculatingScenario.SellDayTrade:
                    return this.ComputeModelTwo(parameter.amount, parameter.unitPrice, parameter.quoteFactor, parameter.stress);
                
                case CalculatingScenario.BuyStrategyDayTrade:
                case CalculatingScenario.SellStrategyDayTrade:
                    return this.ComputeModelThree(parameter.amount, parameter.unitPrice, parameter.quoteFactor, parameter.stress, parameter.lossPrice);

                case CalculatingScenario.SellStrategy:
                    return this.ComputeModelFour(parameter.amount, parameter.unitPrice, parameter.quoteFactor, parameter.stress, parameter.lossPrice, parameter.secundaryStress);

                default:
                    return 0;
            }
        }else{

            switch(calculatingScenario){
                case CalculatingScenario.Buy:
                case CalculatingScenario.Sell:
                case CalculatingScenario.BuyDayTrade:
                case CalculatingScenario.SellDayTrade:
                    return this.ComputeFutureModelOne(parameter.amount, parameter.unitPrice, parameter.quoteFactor, parameter.stress, parameter.ptax);

                case CalculatingScenario.BuyStrategyDayTrade:
                case CalculatingScenario.SellStrategyDayTrade:
                    return this.ComputeFutureModelTwo(parameter.amount, parameter.unitPrice, parameter.quoteFactor, parameter.stress, parameter.lossPrice, parameter.ptax);
                
                case CalculatingScenario.BuyStrategy:
                case CalculatingScenario.SellStrategy:
                    return this.ComputeFutureModelThree(parameter.amount, parameter.unitPrice, parameter.quoteFactor, parameter.stress, parameter.lossPrice, parameter.ptax, parameter.secundaryStress);

                default:
                    return 0;
            }
        }
        
    }


    /**
     * Cenários de aplicação: Compra não planejada e Compra planejada (CalculatingScenario.Buy e CalculatingScenario.BuyStrategy)
     * @param amount lote padrão ou a quantidade a ser comprada ou a quantidade executada
     * @param unitPrice última cotação ou preço executado na ordem
     * @param quoteFactor fator de cotação
     */
    public ComputeModelOne(amount: number, unitPrice: number, quoteFactor: number){
        return (amount * unitPrice) / quoteFactor;
    }

     /**
     * Cenários de aplicação: Compra e venda (planejadas e não planejadas em futuros). 
     * CalculatingScenario.Buy, CalculatingScenario.Sell, CalculatingScenario.BuyDayTrade e CalculatingScenario.SellDayTrade
     * @param amount lote padrão ou a quantidade a ser comprada ou a quantidade executada
     * @param unitPrice última cotação ou preço executado na ordem
     * @param quoteFactor fator de cotação
     * @param stress stress percentual do ativo (não planejado, não planejado day-trade e estratégia)
     * @param ptax cotação do dólar ptax
     */
    public ComputeFutureModelOne(amount: number, unitPrice: number, quoteFactor: number, stress: number, ptax?: number){
        ptax = ptax ? ptax : 1;
        return amount * unitPrice * quoteFactor * ptax * (stress / 100);
    }

    /**
     * Cenários de aplicação: Compra/Venda planejada day-trade CalculatingScenario.BuyStrategyDayTrade e CalculatingScenario.SellStrategyDayTrade
     * @param amount lote padrão ou a quantidade a ser comprada ou a quantidade executada
     * @param unitPrice última cotação ou preço executado na ordem
     * @param quoteFactor fator de cotação
     * @param stress stress percentual do ativo (não planejado, não planejado day-trade e estratégia)
     * @param lossPrice preço do stop loss
     * @param ptax cotação do dólar ptax
     */
    public ComputeFutureModelTwo(amount: number, unitPrice: number, quoteFactor: number, stress: number, lossPrice: number, ptax?: number){
        ptax = ptax ? ptax : 1;
        return (amount * unitPrice * quoteFactor * ptax * (stress / 100)) + (amount * Math.abs(unitPrice - lossPrice) * quoteFactor * ptax);
    }

     /**
     * Cenários de aplicação: Compra/Venda planejada CalculatingScenario.BuyStrategy e CalculatingScenario.SellStrategy
     * @param amount lote padrão ou a quantidade a ser comprada ou a quantidade executada
     * @param unitPrice última cotação ou preço executado na ordem
     * @param quoteFactor fator de cotação
     * @param stress stress percentual do ativo (não planejado, não planejado day-trade e estratégia)
     * @param lossPrice preço do stop loss
     * @param ptax cotação do dólar ptax
     */
    public ComputeFutureModelThree(amount: number, unitPrice: number, quoteFactor: number, stress: number, lossPrice: number, ptax?: number, secundaryStress?: number){
        const modelOneResult = this.ComputeFutureModelOne(amount, unitPrice, quoteFactor, secundaryStress, ptax);
        const modelTwoResult = this.ComputeFutureModelTwo(amount, unitPrice, quoteFactor, stress, lossPrice, ptax);

        return Math.max(modelOneResult, modelTwoResult);
    }


    /**
     * Cenários de aplicação: Compra day-trade, venda e venda day-trade 
     * (CalculatingScenario.BuyDayTrade, CalculatingScenario.Sell, CalculatingScenario.SellDayTrade)
     * @param amount lote padrão ou a quantidade a ser comprada ou a quantidade executada
     * @param unitPrice última cotação ou preço executado na ordem
     * @param quoteFactor fator de cotação
     * @param stress stress percentual do ativo (não planejado, não planejado day-trade e estratégia)
     */
    public ComputeModelTwo(amount: number, unitPrice: number, quoteFactor: number, stress: number){
        return ((amount * unitPrice) / quoteFactor) * (stress / 100);
    }

    /**
     * Cenários de aplicação: Compra/venda planejada day-trade (CalculatingScenario.BuyStrategyDayTrade, CalculatingScenario.SellStrategyDayTrade)
     * @param amount lote padrão ou a quantidade a ser comprada ou a quantidade executada
     * @param unitPrice última cotação ou preço executado na ordem
     * @param quoteFactor fator de cotação
     * @param stress stress percentual do ativo (não planejado, não planejado day-trade e estratégia)
     * @param lossPrice preço do stop loss
     */
    public ComputeModelThree(amount: number, unitPrice: number, quoteFactor: number, stress: number, lossPrice: number){
        return ((amount * unitPrice) / quoteFactor) * (stress / 100) + (amount * Math.abs(unitPrice - lossPrice) / quoteFactor);
    }

    /**
     * Cenários de aplicação: Venda planejada (SellStrategy)
     * @param amount lote padrão ou a quantidade a ser comprada ou a quantidade executada
     * @param unitPrice última cotação ou preço executado na ordem
     * @param quoteFactor fator de cotação
     * @param stress stress percentual do ativo (não planejado, não planejado day-trade e estratégia)
     * @param lossPrice preço do stop loss
     */
    public ComputeModelFour(amount: number, unitPrice: number, quoteFactor: number, stress: number, lossPrice: number, secundaryStress: number){
        const modelTwoResult = this.ComputeModelTwo(amount, unitPrice, quoteFactor, secundaryStress);
        const modelThreeResult = this.ComputeModelThree(amount, unitPrice, quoteFactor, stress, lossPrice);
        
        return Math.max(modelTwoResult, modelThreeResult);
    }

    /**
     * Método que calcula o esperado de ganho ou de perda de uma estratégia.
     * @param amount quantidade de ativos 
     * @param quoteFactor fator de cotação
     * @param inputUnitPrice preço da cotação do ativo (atual ou da compra)
     * @param stopUnitPrice preço da cotaçao esperada (de gain, de loss ou o valor que esta resultando no momento)
     * @param future flag que determina se o ativo é futuro ou não
     * @param ptax  valor da cotação da Ptax em ativos que precisam desse valor no calculo (futuros ptax)
     * @param returnAbsValue flag que determina se o valor retornado será valor absoluto (ou seja sem sinal)
     */
    public ComputeStopValue(amount: number, quoteFactor: number, inputUnitPrice: number, stopUnitPrice: number, future: boolean, ptax: number = null, returnAbsValue: boolean = true){
        let ptaxValue = ptax || 1;
        let inputValue = 0;
        let stopValue = 0;
        let result;
        const unitPriceDiff = stopUnitPrice - inputUnitPrice;

        if(future){
            result = (amount * unitPriceDiff) * quoteFactor * ptaxValue;
        }
        else{
            result = (amount * unitPriceDiff) / quoteFactor;
        }

        if(returnAbsValue)
            result = Math.abs(result);

        return result;
    }

    /**
     * Calcula o resultado atual considerando o valor executado e o valor atual do ativo
     * @param amount quantidade de ativos
     * @param quoteFactor fator de cotação
     * @param currentUnitPrice preco atual da cotacao ou valor a ser considerado como valor atual
     * @param executedUnitPrice preco executado da ordem
     * @param future flag que determina se o ativo é Futuro ou não
     * @param isBuy flag que determina se o ativo é compra ou venda
     * @param ptax  valor da cotação da Ptax em ativos que precisam desse valor no calculo (futuros ptax)
     */
    public ComputeResultValue(amount: number, quoteFactor: number, currentUnitPrice: number, executedUnitPrice: number, future: boolean, isBuy:boolean, ptax: number = null){
        let ptaxValue = ptax || 1;
        let inputValue = 0;
        let stopValue = 0;
        let result;
        let unitPriceDiff = currentUnitPrice - executedUnitPrice;
        if(!isBuy)
            unitPriceDiff = executedUnitPrice - currentUnitPrice;

        if(future){
            result = (amount * unitPriceDiff) * quoteFactor * ptaxValue;
        }
        else{
            result = (amount * unitPriceDiff) / quoteFactor;
        }

        return result;
    }

    /** Verifica e retorna o cenário de cálculo para uma operação/ordem com base em informações básicas
     * @param buy compra?
     * @param dayTrade day-trade?
     * @param planned planejada (estratégia)?
     */
    public computeCalculatingScenario(buy: boolean, dayTrade: boolean, planned: boolean): CalculatingScenario{
        if(buy){
            if(planned)
                return dayTrade ? CalculatingScenario.BuyStrategyDayTrade : CalculatingScenario.BuyStrategy;
            
            return dayTrade? CalculatingScenario.BuyDayTrade : CalculatingScenario.Buy;
        }else{
            if(planned)
                return dayTrade ? CalculatingScenario.SellStrategyDayTrade : CalculatingScenario.SellStrategy;

            return dayTrade ? CalculatingScenario.SellDayTrade : CalculatingScenario.Sell;
        }
            
    }

    /** Verifica o cenário de mercado para o cálculo do resultado atual
     * @param typeMarket mercado do instrumento
     */
    public computeMarketScenario(typeMarket: OrderMarketTypeEnum, isPtax?: boolean): MarketScenario{
        
        if(typeMarket == OrderMarketTypeEnum.Future)
            return isPtax? MarketScenario.FuturePtax : MarketScenario.FutureWithoutPtax;

        return MarketScenario.StockAndOptions;
    }

    /**
     * Retorna o potencial de lucro de uma recomendação de bolsa
     * @param marketScenario Cenário do mercado
     * @param calculateScenario Cenário do mercado
     * @param calculateParams Parametros de calculo
     * @param gainPrice Valor de ganho
     * @param isFuture Se é futuro ou não
     */
    public computeProfitPotential(marketScenario: number, calculateScenario: number, calculateParams: ComputingParameter, gainPrice: number, isFuture: boolean, symbol: string) {
        const stopValue = this.ComputeStopValue(calculateParams.amount, calculateParams.quoteFactor, calculateParams.unitPrice, gainPrice, isFuture, calculateParams.ptax);
        const minimumToInvest = this.calculate(marketScenario,calculateScenario,calculateParams);
        const value = ((stopValue / minimumToInvest) * 100);
        return isFinite(value) ? (value.toFixed(2) || 0) : 0;
    }

    /**
    * Retorna o preço médio de uma ação que foi executada
    * @param totalExecutedValue Valor total executado.
    * @param cumQty Quantidade executada.
    */
    public computeMediumPrice(totalExecutedValue: number, cumQty: number) {
        return totalExecutedValue / cumQty;
    }

}